import { inject, Injectable } from '@angular/core';
import { PrecallReportStep } from '../../models/app/precall/precall-report-step';
import { QualityTestService } from './quality-test.service';
import { ConnectivityTestService } from './connectivity-test.service';
import { map, Observable, zip } from 'rxjs';
import { toKbps } from 'src/app/shared/pipes/to-kbps.pipe';
import { toMbps } from 'src/app/shared/pipes/to-mbps.pipe';
import { statusIcon } from 'src/app/shared/pipes/status-icon.pipe';
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private readonly qualityService = inject(QualityTestService);
  private readonly connectivityService = inject(ConnectivityTestService);
  private readonly datePipe = inject(DatePipe);

  subscribe(): Observable<PrecallReportStep> {
    return zip(this.connectivityService.store.data$, this.qualityService.store.data$).pipe(
      map(
        ([connectivity, quality]) =>
          ({
            connectivity,
            quality,
          }) as PrecallReportStep,
      ),
    );
  }

  report(): PrecallReportStep | null {
    const connectivity = this.connectivityService.store.snapshot;
    const quality = this.qualityService.store.snapshot;

    if (!connectivity) {
      return null;
    }
    if (!quality) {
      return null;
    }

    return {
      connectivity,
      quality,
    };
  }

  /**
   * Builds a textual report for email from reportValue data.
   *
   * @param reportValue - The report object containing connectivity and quality test results.
   * @returns The textual report as a string.
   */
  buildEmailReport(reportValue: PrecallReportStep): string {
    // Initialize mail content
    let mailContent = '';

    // Hardware Setup Results
    mailContent += 'Hardware Setup Results:\n';

    if (reportValue.connectivity?.device) {
      const device = reportValue.connectivity.device;
      mailContent += `${statusIcon(device.compatibility)} Device Type: ${device.type}\n`;
    }

    if (reportValue.connectivity?.browser) {
      const browser = reportValue.connectivity.browser;
      mailContent += `${statusIcon(browser.compatibility)} ${browser.type} ${browser.version} is ${browser.compatibility ? '' : 'not '}supported by the Vonage Video API.\n`;
    }

    if (reportValue.connectivity?.webRtc) {
      const webRtc = reportValue.connectivity.webRtc;
      mailContent += `${statusIcon(webRtc.compatibility)} ${webRtc.compatibility ? 'Compatible' : 'Not compatible'} with WebRTC.\n`;
    }

    mailContent += '\nConnectivity Test Results:\n';

    const websocket = reportValue.connectivity?.services?.websocket;
    if (websocket) {
      mailContent += `${statusIcon(websocket.status)} Websocket Status: ${websocket.status}`;
      if (websocket.reason) {
        mailContent += ` - ${websocket.reason}`;
      }
      mailContent += '\n';
    }

    const link = reportValue.connectivity?.services?.link;
    if (link) {
      mailContent += `${statusIcon(link.status)} Link Service Status: ${link.status}`;
      if (link.reason) {
        mailContent += ` - ${link.reason}`;
      }
      mailContent += '\n';
    }

    const video = reportValue.connectivity?.services?.video;
    if (video) {
      mailContent += `${statusIcon(video.status)} Video Service Status: ${video.status}`;
      if (video.reason) {
        mailContent += ` - ${video.reason}`;
      }
      mailContent += '\n';
    }

    mailContent += '\nQuality Test Results:\n';

    mailContent += `- Test : ${statusIcon(reportValue.quality?.status, true)}\n\n`;

    const audioBitrate = reportValue.quality?.audioBitrate;
    if (audioBitrate) {
      const mos = audioBitrate.mos?.toFixed(1) ?? 'N/A';
      const bitrateKbps = toKbps(audioBitrate.complete?.audio?.bitrate);
      const packetLoss = ((audioBitrate.complete?.audio?.packetLossRatio ?? 0) * 100).toFixed(2);

      mailContent += 'Audio\n';
      mailContent += `- Audio Quality: ${mos}\n`;
      mailContent += `- Audio Bitrate: ${bitrateKbps} kbps\n`;
      mailContent += `- Audio Packet Loss: ${packetLoss}%\n\n`;
    }

    mailContent += 'Bandwidth\n';

    const pingMos = reportValue.quality.ping?.mos || 0;
    const downloadMos = reportValue.quality.download?.mos || 0;
    const uploadMos = reportValue.quality.upload?.mos || 0;

    console.log('MOS values:', pingMos, downloadMos, uploadMos);
    const mos = Math.min(pingMos, downloadMos, uploadMos);

    const qualityMos = mos?.toFixed(1) ?? 'N/A';
    mailContent += `- Quality: ${qualityMos}\n`;

    const ping = reportValue.quality?.ping;
    if (ping) {
      mailContent += `- Average ping: ${ping.latencyMs} ms\n`;
      mailContent += `- Minimum ping: ${ping.min} ms\n`;
      mailContent += `- Maximum ping: ${ping.max} ms\n`;
    }

    const download = reportValue.quality?.download;
    if (download) {
      mailContent += `- Downstream flow: ${toMbps(download.bytesPerSecond)} Mbps ${statusIcon(download.status)}\n`;
    }

    const upload = reportValue.quality?.upload;
    if (upload) {
      mailContent += `- Upstream flow: ${toMbps(upload.bytesPerSecond)} Mbps ${statusIcon(upload.status)}\n`;
    }

    mailContent += '\nGeneral:\n';
    const testDate = reportValue.quality?.ping?.timestamp;
    if (testDate) {
      mailContent += `- Test Date: ${this.datePipe.transform(testDate, 'yyyy-MM-dd HH:mm:ss')}\n`;
    }

    return mailContent.trim();
  }
}
